import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { 
  LayoutDashboard, Store, CalendarCheck, BookOpen, DollarSign, 
  FileText, ToggleLeft, ToggleRight, ChevronRight, Settings,
  TrendingUp, Package, Users, Clock, Star, Sparkles, Upload, Camera, MapPin,
  Plus, ChevronDown, Tag, ClipboardCheck, ClipboardList, Truck, FolderOpen, CalendarPlus, BarChart3
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { HomeworkReviewQueue } from '@/components/HomeworkReviewQueue';
import { Button } from '@/components/ui/button';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '@/App';

// Import existing components to embed
import VendorProducts from './marketplace/VendorProducts';
import VendorOrders from './marketplace/VendorOrders';
import VendorShipping from './marketplace/VendorShipping';
import DirectDepositCard from '../components/marketplace/DirectDepositCard';
import SetupChecklist from '../components/practitioner/SetupChecklist';
import VendorEarnings from './marketplace/VendorEarnings';
import VendorBookings from './marketplace/VendorBookings';
import PractitionerEventsPage from './practitioner/PractitionerEventsPage';
import PractitionerPollsPage from './practitioner/PractitionerPollsPage';
import WorkshopCouponsTab from './practitioner/WorkshopCouponsTab';
import VendorAvailability from './marketplace/VendorAvailability';
import IntakeFormsPage from './practitioner/IntakeFormsPage';
import ClientFilesPage from './practitioner/ClientFilesPage';
import VendorReviews from './marketplace/VendorReviews';
import PractitionerSubscription from './marketplace/PractitionerSubscription';
import CouponsDealsTab from './marketplace/CouponsDealsTab';
import StoreSettingsPanel from '@/components/marketplace/StoreSettingsPanel';
import ImageAdjustDialog from '@/components/marketplace/ImageAdjustDialog';
import ParticipantIndexPage from './practitioner/ParticipantIndexPage';
import ContributorDashboard from '../components/ContributorDashboard';
import WorkshopBuilder from '../components/WorkshopBuilder';
import WorkshopEarningsDashboard from '../components/WorkshopEarningsDashboard';
import OnboardingWizard from '../components/OnboardingWizard';
import TutorialsButton from '../components/TutorialsButton';
import WalkthroughButton from '../components/walkthrough/WalkthroughButton';
import SharedResourcesButton from '../components/SharedResourcesButton';
import PromoteSpotlightButton from '../components/spotlights/PromoteSpotlightModal';

const styles = `
  .ph-root {
    min-height: 100vh;
    font-family: 'Nunito Sans', sans-serif;
    color: #f5f0e8;
  }

  .ph-header {
    padding: 24px 32px;
    border-bottom: 1px solid rgba(255,255,255,0.08);
  }

  .ph-header h1 {
    font-family: 'Cormorant Garamond', serif;
    font-size: 28px;
    font-weight: 600;
    margin: 0 0 4px;
  }

  .ph-header p {
    font-size: 13px;
    color: rgba(245,240,232,0.5);
    margin: 0;
  }

  .ph-layout {
    display: flex;
    min-height: calc(100vh - 80px);
  }

  .ph-sidebar {
    width: 220px;
    border-right: 1px solid rgba(255,255,255,0.08);
    padding: 16px 0;
    flex-shrink: 0;
  }

  .ph-nav-item {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 24px;
    font-size: 14px;
    color: rgba(245,240,232,0.6);
    cursor: pointer;
    border: none;
    background: none;
    width: 100%;
    text-align: left;
    transition: all 0.15s;
    font-family: 'Nunito Sans', sans-serif;
  }

  .ph-nav-item:hover {
    color: rgba(245,240,232,0.85);
    background: rgba(255,255,255,0.04);
  }

  .ph-nav-item.active {
    color: #c9a84c;
    background: rgba(201,168,76,0.08);
    border-right: 2px solid #c9a84c;
  }

  .ph-nav-section {
    padding: 8px 24px 4px;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 1.2px;
    text-transform: uppercase;
    color: rgba(245,240,232,0.3);
    margin-top: 12px;
  }

  .ph-content {
    flex: 1;
    padding: 0;
    overflow-y: auto;
  }

  /* Overview Cards */
  .ph-overview {
    padding: 32px;
  }

  .ph-stats-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 16px;
    margin-bottom: 32px;
  }

  .ph-stat-card {
    background: rgba(255,255,255,0.04);
    border: 1px solid rgba(255,255,255,0.08);
    border-radius: 12px;
    padding: 20px;
  }

  .ph-stat-label {
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.8px;
    text-transform: uppercase;
    color: rgba(245,240,232,0.4);
    margin-bottom: 8px;
  }

  .ph-stat-value {
    font-family: 'Cormorant Garamond', serif;
    font-size: 28px;
    font-weight: 600;
    color: #f5f0e8;
  }

  /* Feature Toggles */
  .ph-toggles {
    padding: 32px;
  }

  .ph-toggle-card {
    background: rgba(255,255,255,0.04);
    border: 1px solid rgba(255,255,255,0.08);
    border-radius: 12px;
    padding: 20px 24px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 12px;
  }

  .ph-toggle-info {
    display: flex;
    align-items: center;
    gap: 16px;
  }

  .ph-toggle-icon {
    width: 40px;
    height: 40px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .ph-toggle-title {
    font-size: 15px;
    font-weight: 700;
    color: #f5f0e8;
    margin-bottom: 2px;
  }

  .ph-toggle-desc {
    font-size: 12px;
    color: rgba(245,240,232,0.45);
  }

  .ph-toggle-switch {
    width: 44px;
    height: 24px;
    border-radius: 12px;
    border: none;
    cursor: pointer;
    position: relative;
    transition: background 0.2s;
    padding: 0;
  }

  .ph-toggle-switch.on {
    background: #c9a84c;
  }

  .ph-toggle-switch.off {
    background: rgba(255,255,255,0.15);
  }

  .ph-toggle-knob {
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: white;
    position: absolute;
    top: 3px;
    transition: left 0.2s;
  }

  .ph-toggle-switch.on .ph-toggle-knob { left: 23px; }
  .ph-toggle-switch.off .ph-toggle-knob { left: 3px; }

  @media (max-width: 768px) {
    .ph-layout { flex-direction: column; }
    .ph-sidebar { 
      width: 100%; 
      border-right: none; 
      border-bottom: 1px solid rgba(255,255,255,0.08);
      display: flex;
      flex-wrap: nowrap;
      overflow-x: auto;
      -webkit-overflow-scrolling: touch;
      padding: 8px;
      gap: 4px;
    }
    .ph-nav-item { 
      white-space: nowrap; 
      padding: 8px 14px;
      font-size: 13px;
      border-right: none !important;
      flex-shrink: 0;
    }
    .ph-nav-item.active { border-bottom: 2px solid #c9a84c; border-right: none; }
    .ph-nav-section { display: none; }
    /* L5: the trailing tabs were clipped with nothing to say the row scrolls.
       A slim gold bar under the row makes it obvious there is more to the right. */
    .ph-sidebar { scrollbar-width: thin; scrollbar-color: rgba(201,168,76,0.55) transparent; }
    .ph-sidebar::-webkit-scrollbar { height: 4px; }
    .ph-sidebar::-webkit-scrollbar-track { background: rgba(255,255,255,0.05); }
    .ph-sidebar::-webkit-scrollbar-thumb { background: rgba(201,168,76,0.55); border-radius: 999px; }
    .ph-nav-item:last-child { margin-right: 12px; }
  }
`;

function ToggleSwitch({ on, onToggle, testId }) {
  return (
    <button className={`ph-toggle-switch ${on ? 'on' : 'off'}`} onClick={onToggle} data-testid={testId}>
      <div className="ph-toggle-knob" />
    </button>
  );
}

const US_STATES = [
  "AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD",
  "MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC",
  "SD","TN","TX","UT","VT","VA","WA","WV","WI","WY","DC",
];

function ShippingAddressEditor({ vendorData, onUpdate }) {
  const [addr, setAddr] = useState(vendorData?.shipping_address || { street: "", city: "", state: "", zip_code: "", country: "US" });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const update = (field, value) => setAddr(prev => ({ ...prev, [field]: value }));

  const save = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem("token");
      await axios.patch(`${API}/vendor/me`, { shipping_address: addr }, { headers: { Authorization: `Bearer ${token}` } });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
      onUpdate();
    } catch (e) { console.error(e); }
    setSaving(false);
  };

  const hasAddress = addr.street && addr.city && addr.state && addr.zip_code;

  return (
    <div style={{ marginTop: 32 }} data-testid="shipping-address-editor">
      <h3 style={{ fontSize: 14, fontWeight: 700, letterSpacing: "0.5px", textTransform: "uppercase", color: "rgba(245,240,232,0.4)", marginBottom: 16, display: "flex", alignItems: "center", gap: 8 }}>
        <MapPin size={14} /> Ship-from Address
      </h3>
      <div style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 12, padding: 20 }}>
        {!hasAddress && (
          <p style={{ fontSize: 12, color: "#e85d4a", marginBottom: 12, fontWeight: 600 }}>
            Set your ship-from address to enable shipping rate calculation for customers.
          </p>
        )}
        <div style={{ display: "grid", gap: 12 }}>
          <input
            placeholder="Street address"
            value={addr.street}
            onChange={e => update("street", e.target.value)}
            data-testid="hub-ship-street"
            style={{ width: "100%", padding: "10px 14px", borderRadius: 8, border: "1px solid rgba(255,255,255,0.1)", background: "rgba(255,255,255,0.04)", color: "rgba(245,240,232,0.9)", fontSize: 14 }}
          />
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <input
              placeholder="City"
              value={addr.city}
              onChange={e => update("city", e.target.value)}
              data-testid="hub-ship-city"
              style={{ width: "100%", padding: "10px 14px", borderRadius: 8, border: "1px solid rgba(255,255,255,0.1)", background: "rgba(255,255,255,0.04)", color: "rgba(245,240,232,0.9)", fontSize: 14 }}
            />
            <select
              value={addr.state}
              onChange={e => update("state", e.target.value)}
              data-testid="hub-ship-state"
              style={{ width: "100%", padding: "10px 14px", borderRadius: 8, border: "1px solid rgba(255,255,255,0.1)", background: "rgba(255,255,255,0.04)", color: "rgba(245,240,232,0.9)", fontSize: 14 }}
            >
              <option value="">State</option>
              {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <input
              placeholder="ZIP code"
              value={addr.zip_code}
              onChange={e => update("zip_code", e.target.value)}
              data-testid="hub-ship-zip"
              style={{ width: "100%", padding: "10px 14px", borderRadius: 8, border: "1px solid rgba(255,255,255,0.1)", background: "rgba(255,255,255,0.04)", color: "rgba(245,240,232,0.9)", fontSize: 14 }}
            />
            <input
              value="United States"
              disabled
              style={{ width: "100%", padding: "10px 14px", borderRadius: 8, border: "1px solid rgba(255,255,255,0.06)", background: "rgba(255,255,255,0.02)", color: "rgba(245,240,232,0.4)", fontSize: 14 }}
            />
          </div>
        </div>
        <button
          onClick={save}
          disabled={saving}
          data-testid="save-shipping-address"
          style={{
            marginTop: 14, padding: "9px 20px", borderRadius: 8, border: "none",
            background: saved ? "#3d9485" : "rgba(91,181,162,0.15)",
            color: saved ? "white" : "rgba(91,181,162,0.9)",
            fontSize: 13, fontWeight: 600, cursor: "pointer",
            transition: "all 0.2s",
          }}
        >
          {saving ? "Saving..." : saved ? "Saved!" : "Save Address"}
        </button>
      </div>
    </div>
  );
}


// Stripe's return_url and our own deep links land here with ?tab=... — honour it so a
// redirect-based payment comes back to the screen the practitioner started on.
const TAB_ALIASES = {
  subscription: 'booking-subscription',
  // The calendar became the top of Bookings rather than a page of its own, so old links hold.
  'session-calendar': 'manage-bookings',
};

export default function PractitionerHub({ user }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedTab = searchParams.get('tab');
  const [activeTab, setActiveTab] = useState(
    requestedTab ? (TAB_ALIASES[requestedTab] || requestedTab) : 'overview'
  );

  useEffect(() => {
    if (!requestedTab) return;
    setActiveTab(TAB_ALIASES[requestedTab] || requestedTab);
  }, [requestedTab]);
  const [features, setFeatures] = useState({
    storefront: false,
    bookings: false,
    workshops: true,
  });
  // Tier gate: only Free-Comp members and Admins can enable bookings/workshops.
  // Default to derived value from `user` prop; backend confirms via /practitioner-prefs.
  const initialEligible = (() => {
    const role = (user?.role || '').toLowerCase();
    const tier = (user?.membership_tier || '').toLowerCase();
    return role === 'admin' || tier === 'free_comp';
  })();
  const [canEnablePractitioner, setCanEnablePractitioner] = useState(initialEligible);
  const [vendorData, setVendorData] = useState(null);
  const [storefronts, setStorefronts] = useState([]);
  const [storefrontMeta, setStorefrontMeta] = useState({ tier_allowance: 1, can_add_more: false, extra_count: 0, extra_monthly_total: 0 });
  const [showStorefrontDropdown, setShowStorefrontDropdown] = useState(false);
  const [earnings, setEarnings] = useState({ this_month: 0, lifetime: 0, pending: 0 });
  // Pre-launch: bookings are free and Stripe is in test mode, so we tell them to hold off
  // on bank details rather than let them verify an account that won't carry over.
  const [prelaunchFree, setPrelaunchFree] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showWizard, setShowWizard] = useState(false);
  const [logoUrl, setLogoUrl] = useState(null);
  const [logoAdjust, setLogoAdjust] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const navigate = useNavigate();
  const [showNewStoreForm, setShowNewStoreForm] = useState(false);
  const [newStoreError, setNewStoreError] = useState('');
  const [creatingStore, setCreatingStore] = useState(false);
  const [quickAdd, setQuickAdd] = useState(null);   // {eligible, reason, source_store}
  const [newStore, setNewStore] = useState({ store_name: '', store_description: '', store_slug: '' });
  const [loadingTaxLink, setLoadingTaxLink] = useState(false);

  const openTaxDocuments = async () => {
    setLoadingTaxLink(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API}/vendor/stripe/tax-documents-link`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      window.open(res.data.url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      const msg = err.response?.data?.detail
        || 'Unable to open your Stripe dashboard. Make sure Stripe Connect onboarding is complete.';
      window.alert(msg);
    } finally {
      setLoadingTaxLink(false);
    }
  };

  const fetchData = useCallback(async () => {
    const token = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}` };
    let hasApprovedStorefront = false;

    try {
      // Fetch all storefronts for this user
      const sfRes = await axios.get(`${API}/vendor/storefronts`, { headers }).catch(() => null);
      if (sfRes?.data) {
        const sfs = sfRes.data.storefronts || [];
        setStorefronts(sfs);
        setStorefrontMeta({
          tier_allowance: sfRes.data.tier_allowance || 1,
          can_add_more: sfRes.data.can_add_more || false,
          tier: sfRes.data.tier,
          is_admin: sfRes.data.is_admin || false,
          extra_count: sfRes.data.extra_count || 0,
          extra_monthly_total: sfRes.data.extra_monthly_total || 0,
          extra_storefront_fee: sfRes.data.extra_storefront_fee || 5.0,
        });

        // Set active storefront from localStorage or first approved
        const savedId = localStorage.getItem('activeVendorId');
        const approved = sfs.filter(s => s.status === 'approved');
        let active = approved.find(s => s.id === savedId) || approved[0];
        if (active) {
          localStorage.setItem('activeVendorId', active.id);
          setVendorData(active);
          hasApprovedStorefront = true;
          setFeatures(prev => ({ ...prev, storefront: true }));
          if (active.avatar_url) setLogoUrl(active.avatar_url);
        }

        // Fetch earnings for active storefront
        if (active) {
          const earningsRes = await axios.get(`${API}/vendor/earnings/summary`, { headers }).catch(() => null);
          if (earningsRes?.data) {
            setEarnings({
              this_month: earningsRes.data.current_month_net || 0,
              lifetime: earningsRes.data.lifetime_net || 0,
              pending: earningsRes.data.pending_transfer || 0,
            });
          }
        }
      } else {
        // No storefronts at all — check for single vendor (backwards compat)
        const vendorRes = await axios.get(`${API}/vendor/me`, { headers }).catch(() => null);
        if (vendorRes?.data) {
          setVendorData(vendorRes.data);
          setStorefronts([vendorRes.data]);
          hasApprovedStorefront = vendorRes.data.status === 'approved';
          setFeatures(prev => ({ ...prev, storefront: true }));
          if (vendorRes.data.avatar_url) setLogoUrl(vendorRes.data.avatar_url);
          localStorage.setItem('activeVendorId', vendorRes.data.id);
        }
      }

      // Load saved feature preferences
      const prefsRes = await axios.get(`${API}/user/practitioner-prefs`, { headers }).catch(() => null);
      if (prefsRes?.data) {
        const eligible = prefsRes.data._can_enable_practitioner !== false;
        setCanEnablePractitioner(eligible);
        const cleaned = { ...prefsRes.data };
        if (!eligible) {
          cleaned.bookings = false;
          cleaned.workshops = false;
        }
        // An approved storefront always wins over a stale/absent preference row,
        // otherwise this merge switches the Storefront section straight back off.
        if (hasApprovedStorefront) cleaned.storefront = true;
        setFeatures(prev => ({ ...prev, ...cleaned }));
        if (!prefsRes.data._saved && !hasApprovedStorefront) {
          setShowWizard(true);
        }
      } else if (!hasApprovedStorefront) {
        setShowWizard(true);
      }
    } catch (err) {
      console.error('Failed to load practitioner data:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Close storefront dropdown when clicking outside
  useEffect(() => {
    const handler = (e) => {
      if (showStorefrontDropdown && !e.target.closest('[data-testid="storefront-switcher"]')) {
        setShowStorefrontDropdown(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showStorefrontDropdown]);

  // A practitioner who has just switched Bookings on is shown the subscription there and
  // then. In-app bookings need it, and the alternative was building a whole schedule only
  // to have Save refused. Nobody is sent here twice: once the subscription is active
  // (or they are comped/exempt) this does nothing, and the page stays a menu item they
  // visit when they want to change plan.
  useEffect(() => {
    const token = localStorage.getItem('token');
    axios.get(`${API}/practitioner/subscription/status`, { headers: { Authorization: `Bearer ${token}` } })
      .then(({ data }) => setPrelaunchFree(!!data?.prelaunch_free))
      .catch(() => {});
  }, []);

  const landOnSubscriptionIfNeeded = async () => {
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.get(`${API}/practitioner/subscription/status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (data?.active) return false;
      setActiveTab('booking-subscription');
      toast.info('Bookings is on. Choose a plan to accept in-app bookings — external booking links stay free.');
      return true;
    } catch {
      return false;   // a failed status check must never strand them on a page they did not pick
    }
  };

  const toggleFeature = async (key) => {
    // Tier gate: bookings/workshops only for Free-Comp / Admin
    if ((key === 'bookings' || key === 'workshops') && !canEnablePractitioner) {
      return;
    }
    const newVal = !features[key];

    // Turning the storefront on used to save a preference and nothing else: the Storefront
    // nav appeared, every screen behind it failed, and the practitioner had to go to the
    // Marketplace and "request" a store (owner, Jun 2026: "Why do they have the ability to
    // toggle a store on, but then have to go into the Marketplace to request a store?").
    // Now the toggle asks for a name and opens the store right here.
    if (key === 'storefront' && newVal && !vendorData) {
      setShowNewStoreForm(true);
      return;
    }

    setFeatures(prev => ({ ...prev, [key]: newVal }));

    const token = localStorage.getItem('token');
    await axios.post(`${API}/user/practitioner-prefs`,
      { [key]: newVal },
      { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
    ).catch(() => {});

    if (key === 'bookings' && newVal) {
      await landOnSubscriptionIfNeeded();
    }
  };

  // The Hub's own logo button used to post the raw file, so a logo changed here skipped the
  // position-and-zoom step that Store Settings offers — the same picture, framed two different
  // ways depending on which button you happened to use (owner, Oct 2026). One path now.
  const handleLogoUpload = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setLogoAdjust({ src: URL.createObjectURL(file), file });
  };

  const saveAdjustedLogo = async (blob, frame) => {
    setUploadingLogo(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', new File([blob], 'logo.png', { type: 'image/png' }));
      formData.append('original', logoAdjust.file);
      formData.append('crop', JSON.stringify(frame));
      const res = await axios.post(`${API}/vendor/logo?vendor_id=${vendorData?.id || ''}`, formData, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data?.file_url) {
        setLogoUrl(res.data.file_url);
      }
      if (logoAdjust.src?.startsWith('blob:')) URL.revokeObjectURL(logoAdjust.src);
      setLogoAdjust(null);
    } catch (err) {
      console.error('Logo upload failed:', err);
      alert('Upload failed — please use JPG, PNG, GIF, or WebP under 5 MB.');
    } finally {
      setUploadingLogo(false);
    }
  };

  const switchStorefront = (sf) => {
    localStorage.setItem('activeVendorId', sf.id);
    setVendorData(sf);
    if (sf.avatar_url) setLogoUrl(sf.avatar_url);
    else setLogoUrl(null);
    setShowStorefrontDropdown(false);
    // Re-fetch earnings for the new storefront
    const token = localStorage.getItem('token');
    axios.get(`${API}/vendor/earnings/summary`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setEarnings({
        this_month: r.data.current_month_net || 0,
        lifetime: r.data.lifetime_net || 0,
        pending: r.data.pending_transfer || 0,
      }))
      .catch(() => {});
  };

  // Ask up front whether paperwork can be reused, so the modal can send people
  // with nothing on file to the full application instead of letting them submit
  // into a dead end. (Insurance stopped being collected in session 70b; the
  // reusable paperwork is now the return policy.)
  useEffect(() => {
    if (!showNewStoreForm) return;
    setQuickAdd(null);
    setNewStoreError('');
    axios.get(`${API}/vendor/quick-add-eligibility`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
    })
      .then(r => setQuickAdd(r.data))
      .catch(() => setQuickAdd({ eligible: false, reason: null }));
  }, [showNewStoreForm]);

  const goToFullApplication = () => {
    const params = new URLSearchParams();
    if (newStore.store_name) params.set('store_name', newStore.store_name);
    if (newStore.store_description) params.set('store_description', newStore.store_description);
    setShowNewStoreForm(false);
    navigate(`/dashboard/marketplace/vendor/apply${params.toString() ? `?${params}` : ''}`);
  };

  const createNewStorefront = async () => {
    if (!newStore.store_name.trim()) return;
    if (quickAdd && !quickAdd.eligible) { goToFullApplication(); return; }
    const token = localStorage.getItem('token');
    setCreatingStore(true);
    setNewStoreError('');
    try {
      const slug = newStore.store_slug || newStore.store_name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
      // Insurance and return policy are inherited server-side from the storefront
      // already on file — same legal entity, same certificate.
      const res = await axios.post(`${API}/vendor/apply`, {
        store_name: newStore.store_name,
        store_description: newStore.store_description || '',
        store_slug: slug,
        categories: [],
        product_types: ['physical', 'digital'],
      }, { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } });
      if (res.data) {
        setShowNewStoreForm(false);
        setNewStore({ store_name: '', store_description: '', store_slug: '' });
        // Opening a store from the toggle has to leave the preference on, or the Storefront
        // nav vanishes again the moment they reload.
        await axios.post(`${API}/user/practitioner-prefs`, { storefront: true },
          { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
        ).catch(() => {});
        setFeatures(prev => ({ ...prev, storefront: true }));
        toast.success(
          res.data.status === 'approved'
            ? 'Your storefront is open — add products whenever you like'
            : "Your storefront is in for review. We'll let you know as soon as it's approved."
        );
        fetchData();
      }
    } catch (e) {
      const detail = e.response?.data?.detail || '';
      // Safety net: if the paperwork turns out to be missing, hand them the real form
      if (typeof detail === 'string' && detail.includes('Insurance documentation is required')) {
        setCreatingStore(false);
        goToFullApplication();
        return;
      }
      setNewStoreError(detail || 'Failed to create storefront. Please try again.');
    } finally {
      setCreatingStore(false);
    }
  };

  const handleWizardComplete = async ({ features: wizFeatures, storeInfo }) => {
    // Save feature preferences
    setFeatures(wizFeatures);
    const token = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

    // Open their storefront with the name they just typed, BEFORE saving preferences —
    // saving prefs provisions one automatically for bookings/workshops, and that fallback
    // would otherwise name the store after their profile instead of their choice.
    // Insurance and a return policy are no longer required (Etsy-style, owner Jun 2026),
    // so the wizard can collect everything it needs.
    let storeError = null;
    if (wizFeatures.storefront && !vendorData) {
      try {
        await axios.post(`${API}/vendor/apply`, {
          store_name: storeInfo?.store_name || `${user?.full_name || 'My'} Store`,
          store_description: storeInfo?.store_description || '',
        }, { headers });
      } catch (err) {
        storeError = err.response?.data?.detail || 'We need a few more details for your storefront';
      }
    }

    await axios.post(`${API}/user/practitioner-prefs`, wizFeatures, { headers }).catch(() => {});
    await fetchData();

    setShowWizard(false);

    if (storeError) {
      const params = new URLSearchParams();
      if (storeInfo?.store_name) params.set('store_name', storeInfo.store_name);
      if (storeInfo?.store_description) params.set('store_description', storeInfo.store_description);
      toast.error(storeError);
      navigate(`/dashboard/marketplace/vendor/apply${params.toString() ? `?${params}` : ''}`);
      return;
    }
    if (wizFeatures.storefront) {
      toast.success('Your storefront is open');
    }
    // Straight from the welcome wizard to the subscription when they asked for Bookings.
    if (wizFeatures.bookings) {
      await landOnSubscriptionIfNeeded();
    }
  };

  const handleWizardSkip = async () => {
    // Save default prefs so wizard doesn't show again
    const token = localStorage.getItem('token');
    await axios.post(`${API}/user/practitioner-prefs`, features, {
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
    }).catch(() => {});
    setShowWizard(false);
  };

  const navItems = [
    { id: 'overview', label: 'Overview', icon: LayoutDashboard, always: true },
    { id: 'settings', label: 'Feature Settings', icon: Settings, always: true },
    // Community events: Free-Comp practitioners and Admin, the people with a Hub.
    { id: 'events', label: 'Events', icon: CalendarPlus, practitionerOnly: true },
    { id: 'polls', label: 'Polls', icon: BarChart3, practitionerOnly: true },
    // Coaching section
    { id: 'participants', label: 'Participants', icon: ClipboardList, practitionerOnly: true, section: 'Coaching' },
    { id: 'homework-review', label: 'Homework Review', icon: ClipboardCheck, practitionerOnly: true, section: 'Coaching' },
    // Bookings section
    { id: 'availability', label: 'Availability', icon: Clock, requires: 'bookings' },
    { id: 'client-forms', label: 'Client Forms', icon: ClipboardList, requires: 'bookings' },
    { id: 'client-files', label: 'Client Files', icon: FolderOpen, requires: 'bookings' },
    { id: 'manage-bookings', label: 'Bookings/Calendar', icon: CalendarCheck, requires: 'bookings' },
    { id: 'booking-subscription', label: 'Subscription', icon: Sparkles, requires: 'bookings' },
    // Workshops section
    { id: 'workshop-builder', label: 'Workshop Builder', icon: BookOpen, requires: 'workshops' },
    { id: 'workshop-earnings', label: 'Workshop Earnings', icon: DollarSign, requires: 'workshops' },
    { id: 'workshop-coupons', label: 'Discount Codes', icon: Tag },
    { id: 'content', label: 'Contributor Content', icon: FileText, requires: 'workshops' },
    // Storefront section
    { id: 'products', label: 'Products', icon: Package, requires: 'storefront' },
    { id: 'orders', label: 'Orders', icon: Store, requires: 'storefront' },
    { id: 'shipping', label: 'Shipping', icon: Truck, requires: 'storefront' },
    { id: 'reviews', label: 'Reviews', icon: Star, requires: 'storefront' },
    { id: 'coupons-deals', label: 'Coupons & Deals', icon: Tag, requires: 'storefront' },
    { id: 'store-settings', label: 'Store Settings', icon: Settings, requires: 'storefront' },
    // Earnings
    { id: 'earnings', label: 'Earnings', icon: DollarSign, always: true },
  ];

  // Homework Review and Shared Resources are practitioner tools — a storefront-only
  // vendor has no clients or partner practitioners, so they never see them.
  const visibleNavItems = navItems.filter(item => {
    if (item.practitionerOnly) return canEnablePractitioner;
    return item.always || features[item.requires];
  });

  // Group nav items by section
  const sections = [];
  let currentSection = { label: null, items: [] };
  
  visibleNavItems.forEach(item => {
    const sectionKey = item.requires || null;
    const sectionLabel = item.section || (item.requires ? { storefront: 'Storefront', bookings: 'Bookings', workshops: 'Workshops' }[item.requires] : null);
    
    if (currentSection.label !== sectionLabel) {
      if (currentSection.items.length > 0) sections.push(currentSection);
      currentSection = { label: sectionLabel, items: [item] };
    } else {
      currentSection.items.push(item);
    }
  });
  if (currentSection.items.length > 0) sections.push(currentSection);

  const renderContent = () => {
    const storeKey = vendorData?.id || 'no-store';
    switch (activeTab) {
      case 'participants':
        return canEnablePractitioner ? <ParticipantIndexPage embedded /> : null;
      case 'homework-review':
        return canEnablePractitioner ? <HomeworkReviewQueue /> : null;
      case 'overview':
        return (
          <div className="ph-overview">
            <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 24, marginBottom: 24 }}>
              Welcome back, {user?.full_name?.split(' ')[0] || (canEnablePractitioner ? 'Practitioner' : 'Vendor')}
            </h2>

            {/* What they still have to do, driven by real data — the onboarding flow has a
                "Finish later" and nothing used to bring them back. */}
            <SetupChecklist
              onNavigateTab={setActiveTab}
              hub={canEnablePractitioner ? 'practitioner' : 'marketplace'}
            />

            {/* How they get paid, on the first screen they see — it used to be buried in the
                Contributor dashboard under Earnings, where no practitioner would look. */}
            <DirectDepositCard prelaunch={prelaunchFree} />

            <div className="ph-stats-grid">
              <div className="ph-stat-card">
                <div className="ph-stat-label">This Month</div>
                <div className="ph-stat-value">${earnings.this_month.toFixed(2)}</div>
              </div>
              <div className="ph-stat-card">
                <div className="ph-stat-label">Lifetime Earnings</div>
                <div className="ph-stat-value">${earnings.lifetime.toFixed(2)}</div>
              </div>
              <div className="ph-stat-card">
                <div className="ph-stat-label">Pending Payout</div>
                <div className="ph-stat-value">${earnings.pending.toFixed(2)}</div>
              </div>
              <div className="ph-stat-card">
                <div className="ph-stat-label">Active Features</div>
                <div className="ph-stat-value">
                  {Object.values(features).filter(Boolean).length}
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <h3 style={{ fontSize: 14, fontWeight: 700, letterSpacing: '0.5px', textTransform: 'uppercase', color: 'rgba(245,240,232,0.4)', marginBottom: 16 }}>
              Quick Actions
            </h3>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              {features.storefront && (
                <Button
                  variant="outline"
                  className="text-white border-white/10 hover:bg-white/5"
                  onClick={() => setActiveTab('products')}
                  data-testid="quick-action-products"
                >
                  <Package className="w-4 h-4 mr-2" /> Manage Products
                </Button>
              )}
              {features.bookings && (
                <Button
                  variant="outline"
                  className="text-white border-white/10 hover:bg-white/5"
                  onClick={() => setActiveTab('availability')}
                  data-testid="quick-action-availability"
                >
                  <CalendarCheck className="w-4 h-4 mr-2" /> Set Availability
                </Button>
              )}
              {features.workshops && (
                <Button
                  variant="outline"
                  className="text-white border-white/10 hover:bg-white/5"
                  onClick={() => setActiveTab('workshop-builder')}
                  data-testid="quick-action-content"
                >
                  <BookOpen className="w-4 h-4 mr-2" /> Workshop Builder
                </Button>
              )}
              <Button
                variant="outline"
                className="text-white border-white/10 hover:bg-white/5"
                onClick={() => setActiveTab('earnings')}
                data-testid="quick-action-earnings"
              >
                <DollarSign className="w-4 h-4 mr-2" /> View Earnings
              </Button>
              <Button
                variant="outline"
                className="text-white border-white/10 hover:bg-white/5"
                onClick={openTaxDocuments}
                disabled={loadingTaxLink}
                data-testid="quick-action-tax-documents"
              >
                <FileText className="w-4 h-4 mr-2" />
                {loadingTaxLink ? 'Opening…' : 'View My Tax Documents'}
              </Button>
              <TutorialsButton />
              <WalkthroughButton hub={canEnablePractitioner ? 'practitioner' : 'marketplace'} />
              {canEnablePractitioner && <SharedResourcesButton />}
              <PromoteSpotlightButton />
            </div>

            {/* Ship-from Address */}
            {features.storefront && vendorData && (
              <ShippingAddressEditor vendorData={vendorData} onUpdate={fetchData} />
            )}
          </div>
        );
      
      case 'settings':
        return (
          <div className="ph-toggles">
            <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 24, marginBottom: 8 }}>
              Feature Settings
            </h2>
            <p style={{ color: 'rgba(245,240,232,0.45)', fontSize: 13, marginBottom: 24 }}>
              Enable or disable sections of your {canEnablePractitioner ? 'Practitioner Hub' : 'Marketplace Hub'}. You can change these at any time.
            </p>
            
            <div className="ph-toggle-card">
              <div className="ph-toggle-info">
                <div className="ph-toggle-icon" style={{ background: 'rgba(26,95,168,0.15)' }}>
                  <Store size={20} color="#4a9eff" />
                </div>
                <div>
                  <div className="ph-toggle-title">Storefront</div>
                  <div className="ph-toggle-desc">Sell physical and digital products through the marketplace</div>
                </div>
              </div>
              <ToggleSwitch on={features.storefront} onToggle={() => toggleFeature('storefront')} testId="storefront-toggle-switch" />
            </div>

            {features.storefront && (
              <div className="ph-toggle-card" style={{ marginLeft: 0, flexDirection: 'column', alignItems: 'flex-start', gap: 12 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, width: '100%' }}>
                  <div className="ph-toggle-icon" style={{ background: 'rgba(26,95,168,0.15)' }}>
                    <Camera size={20} color="#4a9eff" />
                  </div>
                  <div>
                    <div className="ph-toggle-title">Store Logo</div>
                    <div className="ph-toggle-desc">Displayed on your storefront and marketplace listings</div>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 16, paddingLeft: 4 }}>
                  {logoUrl ? (
                    <img
                      src={logoUrl.startsWith('http') ? logoUrl : `${API.replace('/api', '')}${logoUrl}`}
                      alt="Store logo"
                      data-testid="store-logo-preview"
                      style={{ width: 64, height: 64, borderRadius: 10, objectFit: 'cover', border: '2px solid rgba(245,240,232,0.15)' }}
                    />
                  ) : (
                    <div data-testid="store-logo-placeholder" style={{
                      width: 64, height: 64, borderRadius: 10, 
                      background: 'rgba(245,240,232,0.06)', border: '2px dashed rgba(245,240,232,0.15)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      <Store size={24} color="rgba(245,240,232,0.25)" />
                    </div>
                  )}
                  <label
                    data-testid="store-logo-upload-btn"
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: 6,
                      padding: '8px 16px', borderRadius: 8, cursor: 'pointer',
                      background: 'rgba(245,240,232,0.08)', border: '1px solid rgba(245,240,232,0.12)',
                      color: '#f5f0e8', fontSize: 13, fontWeight: 600,
                      transition: 'background 0.2s',
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = 'rgba(245,240,232,0.14)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'rgba(245,240,232,0.08)'}
                  >
                    <Upload size={14} />
                    {uploadingLogo ? 'Uploading...' : logoUrl ? 'Change logo' : 'Upload logo'}
                      <input
                        type="file"
                        accept="image/jpeg,image/png,image/gif,image/webp"
                        onChange={handleLogoUpload}
                        style={{ display: 'none' }}
                        disabled={uploadingLogo}
                        data-testid="hub-logo-file"
                      />
                  </label>
                </div>
              </div>
            )}

            {logoAdjust && (
              <ImageAdjustDialog
                open
                onOpenChange={(next) => {
                  if (next) return;
                  if (logoAdjust.src?.startsWith('blob:')) URL.revokeObjectURL(logoAdjust.src);
                  setLogoAdjust(null);
                }}
                src={logoAdjust.src}
                kind="logo"
                saving={uploadingLogo}
                onSave={saveAdjustedLogo}
              />
            )}

            {canEnablePractitioner && (
              <div className="ph-toggle-card" data-testid="bookings-toggle-card">
                <div className="ph-toggle-info">
                  <div className="ph-toggle-icon" style={{ background: 'rgba(106,31,168,0.15)' }}>
                    <CalendarCheck size={20} color="#a855f7" />
                  </div>
                  <div>
                    <div className="ph-toggle-title">Bookings</div>
                    <div className="ph-toggle-desc">Accept 1:1 session bookings from members and the public</div>
                  </div>
                </div>
                <ToggleSwitch on={features.bookings} onToggle={() => toggleFeature('bookings')} testId="bookings-toggle-switch" />
              </div>
            )}

            {canEnablePractitioner && (
              <div className="ph-toggle-card" data-testid="workshops-toggle-card">
                <div className="ph-toggle-info">
                  <div className="ph-toggle-icon" style={{ background: 'rgba(181,98,42,0.15)' }}>
                    <BookOpen size={20} color="#c9a84c" />
                  </div>
                  <div>
                    <div className="ph-toggle-title">Workshops & Content</div>
                    <div className="ph-toggle-desc">Create and manage workshops, modules, and educational content</div>
                  </div>
                </div>
                <ToggleSwitch on={features.workshops} onToggle={() => toggleFeature('workshops')} testId="workshops-toggle-switch" />
              </div>
            )}
          </div>
        );
      
      // Every storefront-scoped screen is keyed on the active storefront, so switching
      // stores remounts it and it reloads for the store you just picked — no need to
      // navigate away and back.
      case 'products':
        return <VendorProducts key={storeKey} />;
      case 'orders':
        return <VendorOrders key={storeKey} />;
      case 'shipping':
        return <VendorShipping key={storeKey} />;
      case 'reviews':
        return <VendorReviews key={storeKey} />;
      case 'coupons-deals':
        return <CouponsDealsTab key={storeKey} />;
      case 'store-settings':
        return <StoreSettingsPanel key={storeKey} vendorData={vendorData} onUpdate={fetchData} />;
      case 'availability':
        return <VendorAvailability key={storeKey} />;
      case 'client-forms':
        return <IntakeFormsPage />;
      case 'client-files':
        return <ClientFilesPage />;
      case 'manage-bookings':
        return <VendorBookings key={storeKey} />;
      case 'events':
        return <PractitionerEventsPage />;
      case 'polls':
        return <PractitionerPollsPage />;
      case 'booking-subscription':
        return <PractitionerSubscription />;
      case 'workshop-builder':
        return <WorkshopBuilder user={user} />;
      case 'workshop-earnings':
        return <WorkshopEarningsDashboard user={user} />;
      case 'workshop-coupons':
        return <WorkshopCouponsTab />;
      case 'content':
        return <ContributorDashboard user={user} />;
      case 'earnings':
        return <VendorEarnings key={storeKey} />;
      default:
        // An unknown tab used to render nothing at all, so one bad link left the whole hub
        // blank with only the sidebar showing (owner, Jun 2026). Never strand them again.
        return (
          <div className="ph-overview" data-testid="hub-unknown-tab">
            <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 22, marginBottom: 10 }}>
              That section isn&apos;t here
            </h2>
            <p style={{ fontSize: 14, color: 'rgba(245,240,232,0.6)', marginBottom: 18 }}>
              The link you followed points at something that has moved.
            </p>
            <Button
              variant="outline"
              className="text-white border-white/10 hover:bg-white/5"
              onClick={() => setActiveTab('overview')}
              data-testid="hub-unknown-tab-back"
            >
              Back to Overview
            </Button>
          </div>
        );
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <p className="text-gray-400">Loading your hub...</p>
      </div>
    );
  }

  return (
    <>
      <style>{styles}</style>
      <div className="ph-root">
        <div className="ph-header">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <h1>{canEnablePractitioner ? 'Practitioner Hub' : 'Marketplace Hub'}</h1>
              <p>
                {canEnablePractitioner
                  ? 'Manage your workshops, storefront, bookings, and earnings — all in one place'
                  : 'Manage your storefront, products, orders, and earnings — all in one place'}
              </p>
            </div>
            {/* Storefront Switcher */}
            {storefronts.filter(s => s.status === 'approved').length > 0 && (
              <div style={{ position: 'relative' }} data-testid="storefront-switcher">
                <button
                  onClick={() => setShowStorefrontDropdown(!showStorefrontDropdown)}
                  data-testid="storefront-switcher-btn"
                  style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    padding: '10px 16px', borderRadius: 10,
                    background: 'rgba(201,168,76,0.1)', border: '1px solid rgba(201,168,76,0.25)',
                    color: '#f5f0e8', cursor: 'pointer', fontSize: 14, fontWeight: 600,
                    transition: 'background 0.2s',
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = 'rgba(201,168,76,0.18)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'rgba(201,168,76,0.1)'}
                >
                  <Store size={16} color="#c9a84c" />
                  {vendorData?.store_name || 'Select Storefront'}
                  {storefronts.filter(s => s.status === 'approved').length > 1 && <ChevronDown size={14} />}
                </button>

                {showStorefrontDropdown && (
                  <div
                    data-testid="storefront-dropdown"
                    style={{
                      position: 'absolute', top: '100%', right: 0, marginTop: 6,
                      background: '#1a1525', border: '1px solid rgba(201,168,76,0.2)',
                      borderRadius: 12, padding: 8, minWidth: 260, zIndex: 50,
                      boxShadow: '0 12px 32px rgba(0,0,0,0.5)',
                    }}
                  >
                    {storefronts.filter(s => s.status === 'approved').map(sf => (
                      <button
                        key={sf.id}
                        onClick={() => switchStorefront(sf)}
                        data-testid={`storefront-option-${sf.store_slug}`}
                        style={{
                          display: 'flex', alignItems: 'center', gap: 10,
                          width: '100%', padding: '10px 12px', borderRadius: 8,
                          background: sf.id === vendorData?.id ? 'rgba(201,168,76,0.15)' : 'transparent',
                          border: 'none', color: '#f5f0e8', cursor: 'pointer', fontSize: 13,
                          textAlign: 'left', transition: 'background 0.15s',
                        }}
                        onMouseEnter={e => e.currentTarget.style.background = sf.id === vendorData?.id ? 'rgba(201,168,76,0.15)' : 'rgba(255,255,255,0.05)'}
                        onMouseLeave={e => e.currentTarget.style.background = sf.id === vendorData?.id ? 'rgba(201,168,76,0.15)' : 'transparent'}
                      >
                        {sf.avatar_url ? (
                          <img src={sf.avatar_url.startsWith('http') ? sf.avatar_url : `${API.replace('/api', '')}${sf.avatar_url}`}
                            alt="" style={{ width: 28, height: 28, borderRadius: 6, objectFit: 'cover' }} />
                        ) : (
                          <div style={{ width: 28, height: 28, borderRadius: 6, background: 'rgba(201,168,76,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Store size={14} color="#c9a84c" />
                          </div>
                        )}
                        <div>
                          <div style={{ fontWeight: 600 }}>{sf.store_name}</div>
                          <div style={{ fontSize: 11, color: 'rgba(245,240,232,0.4)' }}>/{sf.store_slug}</div>
                        </div>
                        {sf.id === vendorData?.id && <span style={{ marginLeft: 'auto', color: '#c9a84c', fontSize: 11 }}>Active</span>}
                      </button>
                    ))}
                    {storefronts.some(s => s.status === 'pending') && (
                      <div style={{ padding: '8px 12px', fontSize: 12, color: 'rgba(245,240,232,0.35)', borderTop: '1px solid rgba(255,255,255,0.06)', marginTop: 4 }}>
                        {storefronts.filter(s => s.status === 'pending').length} storefront(s) pending approval
                      </div>
                    )}
                    {storefrontMeta.extra_monthly_total > 0 && (
                      <div style={{ padding: '8px 12px', fontSize: 12, color: 'rgba(201,168,76,0.5)', borderTop: '1px solid rgba(255,255,255,0.06)', marginTop: 4 }} data-testid="extra-storefront-charges">
                        {storefrontMeta.extra_count} extra storefront{storefrontMeta.extra_count > 1 ? 's' : ''}: ${storefrontMeta.extra_monthly_total.toFixed(2)}/mo
                      </div>
                    )}
                    {storefrontMeta.can_add_more && (
                      <button
                        onClick={() => { setShowNewStoreForm(true); setShowStorefrontDropdown(false); }}
                        data-testid="add-storefront-btn"
                        style={{
                          display: 'flex', alignItems: 'center', gap: 8,
                          width: '100%', padding: '10px 12px', borderRadius: 8,
                          background: 'transparent', border: 'none', color: '#c9a84c',
                          cursor: 'pointer', fontSize: 13, fontWeight: 600,
                          borderTop: '1px solid rgba(255,255,255,0.06)', marginTop: 4,
                        }}
                      >
                        <Plus size={14} /> Add Storefront
                        {!storefrontMeta.is_admin && storefronts.filter(s => s.status === 'approved').length >= storefrontMeta.tier_allowance && (
                          <span style={{ marginLeft: 'auto', fontSize: 11, color: 'rgba(201,168,76,0.6)' }}>
                            +$5/mo
                          </span>
                        )}
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="ph-layout">
          {/* Sidebar Navigation */}
          <div className="ph-sidebar">
            {sections.map((section, si) => (
              <div key={si}>
                {section.label && (
                  <div className="ph-nav-section">{section.label}</div>
                )}
                {section.items.map(item => {
                  const Icon = item.icon;
                  return (
                    <button
                      key={item.id}
                      className={`ph-nav-item ${activeTab === item.id ? 'active' : ''}`}
                      onClick={() => {
                        setActiveTab(item.id);
                        if (requestedTab) setSearchParams({}, { replace: true });
                      }}
                      data-testid={`ph-nav-${item.id}`}
                    >
                      <Icon size={16} />
                      {item.label}
                    </button>
                  );
                })}
              </div>
            ))}
          </div>

          {/* Content Area */}
          <div className="ph-content">
            {renderContent()}
          </div>
        </div>
      </div>
      {showWizard && (
        <OnboardingWizard
          onComplete={handleWizardComplete}
          onSkip={handleWizardSkip}
          hasVendorAccount={!!vendorData}
          canEnablePractitioner={canEnablePractitioner}
        />
      )}
      {showNewStoreForm && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', 
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100,
        }}
          onClick={(e) => { if (e.target === e.currentTarget) setShowNewStoreForm(false); }}
          data-testid="new-storefront-modal"
        >
          <div style={{
            background: '#1a1525', border: '1px solid rgba(201,168,76,0.2)',
            borderRadius: 16, padding: 32, width: 420, maxWidth: '90vw',
          }}>
            <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 22, marginBottom: 4, color: '#f5f0e8' }}>
              {storefronts.length === 0 ? 'Open your storefront' : 'New Storefront'}
            </h2>
            {(() => {
              const approvedCount = storefronts.filter(s => s.status === 'approved').length;
              const isExtra = !storefrontMeta.is_admin && approvedCount >= storefrontMeta.tier_allowance;
              return (
                <p style={{ fontSize: 13, color: 'rgba(245,240,232,0.45)', marginBottom: 20 }}>
                  {isExtra ? (
                    <>
                      Your <strong style={{ color: '#c9a84c' }}>{storefrontMeta.tier}</strong> tier includes {storefrontMeta.tier_allowance} storefront{storefrontMeta.tier_allowance > 1 ? 's' : ''}.
                      This will be an <strong style={{ color: '#c9a84c' }}>extra storefront</strong> at <strong style={{ color: '#c9a84c' }}>$5/month</strong>, deducted from your payouts.
                    </>
                  ) : storefrontMeta.is_admin ? (
                    <>Admin — no extra charge for additional storefronts.</>
                  ) : storefronts.length === 0 ? (
                    <>Give your store a name and it's open. You can add products, shipping and a
                      logo afterwards — nothing else is needed to get started.</>
                  ) : (
                    <>Create a separate storefront to keep different product lines organized.
                      {approvedCount === storefrontMeta.tier_allowance - 1 && (
                        <span style={{ color: 'rgba(245,240,232,0.35)' }}> This is your last free storefront for your {storefrontMeta.tier} tier.</span>
                      )}
                    </>
                  )}
                </p>
              );
            })()}
            <div style={{ display: 'grid', gap: 12 }}>
              <input
                placeholder="Store Name (e.g., Buelow Wellness)"
                value={newStore.store_name}
                onChange={e => setNewStore(p => ({ ...p, store_name: e.target.value, store_slug: e.target.value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') }))}
                data-testid="new-store-name"
                style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.1)', background: 'rgba(255,255,255,0.04)', color: '#f5f0e8', fontSize: 14 }}
              />
              <div style={{ fontSize: 12, color: 'rgba(245,240,232,0.35)' }}>
                URL: /store/{newStore.store_slug || 'your-store-name'}
              </div>
              <textarea
                placeholder="Brief description of this store..."
                value={newStore.store_description}
                onChange={e => setNewStore(p => ({ ...p, store_description: e.target.value }))}
                data-testid="new-store-desc"
                rows={3}
                style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.1)', background: 'rgba(255,255,255,0.04)', color: '#f5f0e8', fontSize: 14, resize: 'vertical' }}
              />
              <div style={{
                fontSize: 12, color: 'rgba(245,240,232,0.5)', lineHeight: 1.55,
                background: 'rgba(201,168,76,0.08)', border: '1px solid rgba(201,168,76,0.18)',
                borderRadius: 8, padding: '10px 12px',
              }} data-testid="new-store-inherit-note">
                {quickAdd?.source_store
                  ? `Your return policy carries over from ${quickAdd.source_store}. You can change it for this store afterwards in Store Settings.`
                  : 'Make sure you add a return policy for your customers in your storefront settings.'}
              </div>
              {newStoreError && (
                <div style={{
                  fontSize: 12, color: '#fca5a5', background: 'rgba(220,38,38,0.12)',
                  border: '1px solid rgba(220,38,38,0.3)', borderRadius: 8, padding: '10px 12px',
                }} data-testid="new-store-error">
                  {newStoreError}
                </div>
              )}
              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 8 }}>
                <Button variant="outline" className="text-white border-white/10" onClick={() => setShowNewStoreForm(false)} data-testid="new-store-cancel">
                  Cancel
                </Button>
                <Button
                  className="bg-gradient-to-r from-amber-500 to-amber-600 text-white"
                  onClick={createNewStorefront}
                  disabled={!newStore.store_name.trim() || creatingStore}
                  data-testid="new-store-submit"
                >
                  <Plus className="w-4 h-4 mr-2" /> {creatingStore
                    ? 'Opening…'
                    : storefronts.length === 0 ? 'Open my storefront' : 'Create Storefront'}
                  {!storefrontMeta.is_admin && storefronts.filter(s => s.status === 'approved').length >= storefrontMeta.tier_allowance && (
                    <span style={{ marginLeft: 6, opacity: 0.8 }}>· $5/mo</span>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
